import { observer } from 'mobx-react-lite';
/* tslint:disable:jsx-no-lambda WI:47781 */
import * as React from 'react';
import { Pivot, PivotItem } from 'office-ui-fabric-react/lib/Pivot';
import { getInboxClassificationFilterForTable } from 'owa-mail-list-store';
import InboxClassificationFilter from 'owa-mail-inbox-plus-store/lib/store/schema/InboxClassificationFilter';
import {
    lazyShowPivotEnableDisableDialog,
    InboxPlusFirstRunCallout,
} from 'owa-mail-inbox-plus-dialog';
import ReactDOM from 'react-dom';
import inboxPlusStore from 'owa-mail-inbox-plus-store/lib/store/Store';
import InboxPivot from './InboxPivot';
import { lazyOnInboxPivotSelected } from 'owa-mail-inbox-plus-actions';
import getInboxPlusPivotDisplayProperties from 'owa-mail-inbox-plus-store/lib/selectors/getInboxPlusPivotDisplayProperties';
import { ActionButton } from 'office-ui-fabric-react/lib/Button';
import loc from 'owa-localize';
import { inboxPlusCustomize } from 'owa-locstrings/lib/strings/inboxPlusCustomize.locstring.json';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lighted, LightningId } from 'owa-lightning-v2';

const styles = require('./InboxPivotStyles.scss');
const classNames = require('classnames/bind').bind(styles);

export interface InboxPlusHeaderProps {
    tableViewId: string;
    mailListHeaderStylesAsPerUserSettings: string;
}

export default observer(function InboxPlusHeader(props: InboxPlusHeaderProps) {
    const isEasyOn =
        isFeatureEnabled('tri-inboxPlusEasyOnFocusedExclusion') ||
        isFeatureEnabled('tri-inboxPlusEasyOnFocusedInclusion');
    const onPivotItemClicked = async (item: PivotItem) => {
        const classificationFilterToSelect = convertPivotKeyToClassificationViewFilter(
            item.props.itemKey
        );
        const currentClassificationFilter = getInboxClassificationFilterForTable(props.tableViewId);
        const onInboxPivotSelected = await lazyOnInboxPivotSelected.import();
        onInboxPivotSelected(
            currentClassificationFilter,
            classificationFilterToSelect,
            props.tableViewId
        );
    };
    /**
     * Convert pivot key string back to the focused view filter to select
     * @param key of the pivot component
     */
    const convertPivotKeyToClassificationViewFilter = (key: string): InboxClassificationFilter => {
        switch (key) {
            case 'inbox':
                return InboxClassificationFilter.None;
            case 'focused':
                return InboxClassificationFilter.Focused;
            case 'promotions':
                return InboxClassificationFilter.Promotions;
            case 'newsletters':
                return InboxClassificationFilter.Newsletters;
            case 'social':
                return InboxClassificationFilter.Social;
            case 'other':
                return InboxClassificationFilter.Other;
            default:
                throw new Error('Invalid inbox plus pivot');
        }
    };
    const onClickAddButton = async (e: any) => {
        e.stopPropagation();
        const showPivotEnableDisableDialog = await lazyShowPivotEnableDisableDialog.import();
        showPivotEnableDisableDialog(false /* isFromEasyOn */);
        // If user is on the easy on flight, remove their lightning id if they click customize
        // so they do not see the easy on item
        if (isEasyOn) {
            lighted(LightningId.InboxPlusEasyOn, 'Secondary');
        }
    };
    let freTarget: HTMLElement;
    const enabledPivots = inboxPlusStore.inboxPlusPivots.filter(pivotType => pivotType.isEnabled);
    const currentClassificationFilter = getInboxClassificationFilterForTable(props.tableViewId);
    const selectedKey = convertClassificationFiltertoPivotKey(currentClassificationFilter);
    return (
        <div
            className={classNames(
                styles.pivotContainer,
                props.mailListHeaderStylesAsPerUserSettings
            )}>
            <Pivot
                onLinkClick={onPivotItemClicked}
                styles={{
                    link: styles.nonSelectedPivotLink,
                    linkIsSelected: styles.selectedPivotLink,
                    linkContent: styles.linkContent,
                    text: styles.text,
                }}
                selectedKey={selectedKey}>
                {enabledPivots.map(pivot => {
                    const displayProperties = getInboxPlusPivotDisplayProperties(
                        pivot.InboxClassificationFilter
                    );
                    return (
                        <PivotItem
                            key={`${pivot.InboxClassificationFilter}`}
                            headerText={displayProperties.displayText}
                            itemKey={convertClassificationFiltertoPivotKey(
                                pivot.InboxClassificationFilter
                            )}
                            onRenderItemLink={(link, defaultRenderer) => (
                                <InboxPivot
                                    link={link}
                                    defaultRenderer={defaultRenderer}
                                    pivot={pivot}
                                    tableViewId={props.tableViewId}
                                />
                            )}
                            itemIcon={displayProperties.icon}
                        />
                    );
                })}
            </Pivot>
            <ActionButton
                ref={ref => (freTarget = ReactDOM.findDOMNode(ref) as HTMLElement)}
                onClick={onClickAddButton}
                styles={{
                    root: styles.customizeButton,
                    rootHovered: styles.customizeHover,
                    label: styles.buttonText,
                }}>
                {loc(inboxPlusCustomize)}
            </ActionButton>
            {!isEasyOn && (
                <InboxPlusFirstRunCallout
                    lid={LightningId.InboxPlusFRE}
                    when={lightup => setTimeout(lightup, 500)}
                    target={() => freTarget}
                />
            )}
        </div>
    );
});

/**
 * Convert the inbox classification filter to a string that used as the key in Pivot fabric component
 * We need to do this instead of just using the InboxClassificationFilter value, because FocusedViewFilter.None(-1) is ignored by the Fabric Pivot
 */
export function convertClassificationFiltertoPivotKey(
    classificationFitler: InboxClassificationFilter
): string {
    switch (classificationFitler) {
        case InboxClassificationFilter.None:
            return 'inbox';
        case InboxClassificationFilter.Focused:
            return 'focused';
        case InboxClassificationFilter.Promotions:
            return 'promotions';
        case InboxClassificationFilter.Newsletters:
            return 'newsletters';
        case InboxClassificationFilter.Social:
            return 'social';
        case InboxClassificationFilter.Other:
            return 'other';
        default:
            throw new Error('Invalid inbox plus pivot');
    }
}
